import React, { useState, useMemo } from 'react';
import { Input, Space } from 'antd';
import { SearchOutlined, SendOutlined } from '@ant-design/icons';
import Button from 'app/atoms/Button';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Environment, useRTL } from 'configs/constants';

interface GlobalSearchBarProps {
  placeholder?: string;
  buttonText?: string;
  className?: string;
  defaultValue?: string;
}

const GlobalSearchBar: React.FC<GlobalSearchBarProps> = ({
  placeholder,
  buttonText,
  className = '',
  defaultValue = '',
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isRTL = useRTL();
  const [searchText, setSearchText] = useState(defaultValue);
  const [isFocused, setIsFocused] = useState(false);

  const classes = useMemo(() => {
    const baseClasses = `transition-all duration-200 ease-in-out ${className}`;
    if (isFocused) return `${baseClasses} [&_textarea]:!pb-4`;

    // Apply rounded corners based on text direction
    return isRTL ? `${baseClasses} rounded-l-none` : `${baseClasses} rounded-r-none`;
  }, [isFocused, className, isRTL]);

  // Direction-aware padding classes
  const paddingClasses = useMemo(() => {
    const clearIconPadding = isRTL ? '[&_.ant-input-clear-icon]:!pl-3' : '[&_.ant-input-clear-icon]:!pr-3';
    const textAreaPadding = isRTL
      ? '[&_.ant-input]:!pl-8 [&_textarea]:!pl-8 [&_.ant-input]:!pr-3 [&_textarea]:!pr-3'
      : '[&_.ant-input]:!pr-8 [&_textarea]:!pr-8';

    return `${clearIconPadding} ${textAreaPadding}`;
  }, [isRTL]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Check if the related target is the send button or its children
    const isClickingButton = e.relatedTarget?.closest('button');
    if (!e.currentTarget.contains(e.relatedTarget as Node) && !isClickingButton) {
      setIsFocused(false);
    }
  };

  const handleSend = () => {
    if (searchText) {
      // Use URLSearchParams to properly encode the search query
      const searchParams = new URLSearchParams({ q: searchText });
      navigate(`/search?${searchParams.toString()}`);
    }
  };

  // Memoize computed values
  const enterButton = useMemo(() => {
    if (isFocused) return null;
    return (
      <Button
        type="primary"
        icon={<SearchOutlined />}
        size="large"
        onClick={handleSend}
        disabled={import.meta.env.VITE_APP_MODE === Environment.STAGING}
      >
        {buttonText ?? t('dashboard.input.btn')}
      </Button>
    );
  }, [isFocused, buttonText, t, handleSend]);

  return (
    <div className="w-full mb-8 relative">
      <Space.Compact block>
        <Input.TextArea
          placeholder={placeholder ?? t('dashboard.input.placeholder')}
          value={searchText}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onPressEnter={handleSend}
          className={`flex-1 ${classes} ${paddingClasses}`}
          autoSize={{ minRows: isFocused ? 4 : 1, maxRows: isFocused ? 4 : 1 }}
          allowClear
          size="large"
          disabled={import.meta.env.VITE_APP_MODE === Environment.STAGING}
        />
        {enterButton}
      </Space.Compact>
      {isFocused && (
        <Button
          type="text"
          icon={
            <SendOutlined
              className={`text-xl transition-colors duration-200 ${searchText ? 'text-primary-600' : ''} rtl:transform rtl:rotate-180`}
            />
          }
          className={`absolute ${isRTL ? 'left-2' : 'right-2'} bottom-2 flex items-center justify-center w-8 h-8 rounded-full z-10 transition-opacity duration-200 ${!searchText ? 'opacity-50' : ''}`}
          onClick={handleSend}
          size="large"
          disabled={!searchText}
        />
      )}
    </div>
  );
};

export default GlobalSearchBar;
